'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  ApiDocumentationMissingAlert,
  ApplicationEnvironmentEditDialog,
  ApplicationNameEditDialog,
  ApplicationTypeDocumentationEditDialog,
  ApplicationUrlWafStatus,
  BusinessUnitsEditDialog,
  SettingsCard,
  SettingsRow,
  SettingsSection,
  formatBusinessUnitNames,
  resolveApplicationEnvironment,
  toApplicationType,
  useApplicationEnvironmentSelectCollection,
  useApplicationTypeSelectCollection,
} from '@terra/ui-design-system';
import { useApplicationById } from '../../../hooks/api/useApplicationById';
import { useApplicationDocumentationController } from '../../../hooks/api/useApplicationDocumentationController';
import { useApplicationUrlWafStatus } from '../../../hooks/api/useApplicationUrlWafStatus';
import { TargetSourceSurfaceSettingsRow } from './TargetSourceSurfaceSettingsRow';
import { useApplicationGeneralFields } from './useApplicationGeneralFields';

type EditDialog =
  | 'name'
  | 'businessUnits'
  | 'environment'
  | 'applicationType'
  | null;

type TargetApplicationGeneralSectionProps = {
  applicationId: string;
  isReadOnly?: boolean;
  onOpenApiDocumentation: () => void;
};

export function TargetApplicationGeneralSection({
  applicationId,
  isReadOnly,
  onOpenApiDocumentation,
}: TargetApplicationGeneralSectionProps) {
  const t = useTranslations('designSystem.applicationGeneral');
  const tCommon = useTranslations('common');
  const [editDialog, setEditDialog] = useState<EditDialog>(null);
  const { application, isLoading } = useApplicationById({ id: applicationId });
  const documentation = useApplicationDocumentationController({
    applicationId,
  });
  const applicationUrl = application?.url ?? '';
  const applicationUrlWafStatus = useApplicationUrlWafStatus({
    url: applicationUrl,
  });
  const environmentCollection = useApplicationEnvironmentSelectCollection();
  const applicationTypeCollection = useApplicationTypeSelectCollection();
  const fields = useApplicationGeneralFields({ applicationId });
  const applicationName = application?.name ?? '';
  const environment = resolveApplicationEnvironment(application?.environment);
  const applicationType = toApplicationType(application?.isBackendOnly);
  const sourceSurfaceId = application?.sourceSurfaceId;
  const closeDialog = (open: boolean) => {
    if (!open) {
      setEditDialog(null);
    }
  };

  return (
    <SettingsSection title={t('metadata')}>
      <ApiDocumentationMissingAlert
        isBackendOnly={Boolean(application?.isBackendOnly)}
        hasApiDocumentation={documentation.apiDocumentationFiles.length > 0}
        onOpenBusinessContext={onOpenApiDocumentation}
      />
      <SettingsCard isLoading={isLoading}>
        <SettingsRow
          label={t('name')}
          value={applicationName || '-'}
          action={{
            label: tCommon('edit'),
            onClick: () => setEditDialog('name'),
            disabled: fields.isSavingFields,
          }}
          isReadOnly={isReadOnly}
        />
        <SettingsRow
          label={t('url')}
          value={applicationUrl || '-'}
          valueWrap
          description={
            <ApplicationUrlWafStatus
              url={applicationUrl}
              wafStatus={applicationUrlWafStatus}
            />
          }
          tooltip={t('urlDisabledTooltip')}
        />
        <SettingsRow
          label={t('businessUnit')}
          value={formatBusinessUnitNames(fields.assignedBusinessUnits) ?? '-'}
          action={{
            label: tCommon('edit'),
            onClick: () => setEditDialog('businessUnits'),
            disabled: fields.isSavingBusinessUnits,
          }}
          isReadOnly={isReadOnly}
        />
        <SettingsRow
          label={t('environment')}
          value={
            environmentCollection.items.find(
              (item) => item.value === environment,
            )?.label ?? environment
          }
          action={{
            label: tCommon('edit'),
            onClick: () => setEditDialog('environment'),
            disabled: fields.isSavingFields,
          }}
          isReadOnly={isReadOnly}
        />
        <SettingsRow
          label={t('applicationType')}
          value={
            applicationTypeCollection.items.find(
              (item) => item.value === applicationType,
            )?.label
          }
          action={{
            label: tCommon('edit'),
            onClick: () => setEditDialog('applicationType'),
            disabled: fields.isSavingFields,
          }}
          isReadOnly={isReadOnly}
        />
        {sourceSurfaceId ? (
          <TargetSourceSurfaceSettingsRow
            applicationId={applicationId}
            sourceSurfaceId={sourceSurfaceId}
          />
        ) : null}
      </SettingsCard>

      <ApplicationNameEditDialog
        open={editDialog === 'name'}
        name={applicationName}
        loading={fields.isSavingFields}
        onOpenChange={closeDialog}
        onSubmit={(name) => fields.saveFields({ name })}
      />
      <BusinessUnitsEditDialog
        open={editDialog === 'businessUnits'}
        businessUnits={fields.assignedBusinessUnits}
        allBusinessUnits={fields.allBusinessUnits}
        loading={fields.isSavingBusinessUnits}
        disabled={isReadOnly}
        isCreatingBusinessUnit={fields.isCreatingBusinessUnit}
        onOpenChange={closeDialog}
        onCreateBusinessUnit={fields.createBusinessUnitOption}
        onSubmit={fields.saveBusinessUnits}
      />
      <ApplicationEnvironmentEditDialog
        open={editDialog === 'environment'}
        environment={environment}
        loading={fields.isSavingFields}
        onOpenChange={closeDialog}
        onSubmit={(nextEnvironment) =>
          fields.saveFields({ environment: nextEnvironment })
        }
      />
      {editDialog === 'applicationType' ? (
        <ApplicationTypeDocumentationEditDialog
          applicationId={applicationId}
          applicationType={applicationType}
          documentation={documentation}
          isReadOnly={isReadOnly}
          isUpdating={fields.isSavingFields}
          onOpenChange={closeDialog}
          onSave={({ isBackendOnly }) => fields.saveFields({ isBackendOnly })}
        />
      ) : null}
    </SettingsSection>
  );
}
